import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fileUrl } from "@/components/erp/prod/ProdInputs";
import { toast } from "sonner";
import { Modal, Btn, Field, Input } from "@/components/erp/ui";
import { MediaInput } from "@/components/erp/prod/ProdInputs";
import { Play, Pause, Square } from "lucide-react";
import { RetouchDaily } from "@/components/erp/prod/RetouchDaily";

const err = (e) => toast.error(e?.response?.data?.detail || "Gagal");
const secs = (r) => {
  if (!r) return 0;
  const end = r.paused_at ? new Date(r.paused_at) : new Date();
  return Math.max(0, (end - new Date(r.started_at)) / 1000 - (r.paused_s || 0));
};
const hms = (s) => `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const useTick = (on) => { const [, setT] = useState(0); useEffect(() => { if (!on) return undefined; const t = setInterval(() => setT((x) => x + 1), 1000); return () => { clearInterval(t); }; }, [on]); };

export const WoNotes = ({ notes, tid }) => (notes ? <div className="text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1 mt-2" data-testid={tid}><b>Catatan customer:</b> {notes}</div> : null);

export const RetouchProgress = ({ r, tid }) => {
  if (!r || !r.total_g) return null;
  const pct = Math.min(100, Math.round((r.done_g / r.total_g) * 100));
  return (
    <div className="mt-2" data-testid={tid}>
      <div className="flex justify-between text-[11px] text-slate-500"><span>Retouch {Math.round(r.done_g)} / {Math.round(r.total_g)} g · {Math.round(r.minutes)} mnt</span><b>{pct}%</b></div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-sky-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

// Siapa sedang retouch + gram hari ini per orang
export const RetouchTeam = ({ team }) => {
  if (!team?.length) return null;
  return (
    <div className="mb-4 rounded-xl border border-sky-100 bg-sky-50/50 p-3" data-testid="retouch-team">
      <div className="text-xs font-semibold text-sky-800 mb-2">Retouch hari ini</div>
      <div className="flex flex-wrap gap-2">
        {team.map((p) => (
          <div key={p.name} className="rounded-lg bg-white border border-sky-100 px-3 py-1.5 text-xs" data-testid={`retouch-team-${p.name}`}>
            <b>{p.name}</b> · {Math.round(p.today_g)} g · {Math.round(p.minutes)} mnt{p.today_g && p.minutes ? ` · ${Math.round((p.today_g / p.minutes) * 60)} g/jam` : ""}
            {p.active && <span className={`ml-2 px-1.5 rounded ${p.active.status === "PAUSED" ? "bg-amber-100 text-amber-800" : "bg-sky-600 text-white"}`}>{p.active.status === "PAUSED" ? "jeda" : "sedang"} {p.active.wo_code}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

// Bar cepat untuk sesi retouch yang sedang berjalan
export const RetouchBar = ({ r, onOpen }) => {
  useTick(r && r.status === "ACTIVE");
  if (!r) return null;
  return (
    <button type="button" data-testid="retouch-bar" onClick={onOpen} className="w-full mb-4 flex items-center gap-3 rounded-xl bg-sky-600 text-white px-4 py-3 text-left">
      <span className="font-mono text-lg" data-testid="retouch-bar-timer">{hms(secs(r))}</span>
      <span className="text-sm">{r.status === "PAUSED" ? "Retouch dijeda" : "Retouch berjalan"} · {r.wo_code} · {r.file_name}</span>
      <span className="ml-auto text-xs underline">buka</span>
    </button>
  );
};

export const RetouchModal = ({ job, mine, progress, onClose, onSaved }) => {
  const r = mine && mine.job_id === job?.id ? mine : null;
  const [w, setW] = useState(""); const [photos, setPhotos] = useState([]); const [note, setNote] = useState("");
  useEffect(() => { setW(""); setPhotos([]); setNote(""); }, [job?.id]);
  useTick(r && r.status === "ACTIVE");
  const call = async (url, body, msg) => { try { await api.post(url, body); if (msg) toast.success(msg); onSaved(); } catch (e) { err(e); } };
  const finish = async () => {
    try { await api.post(`/prod/retouch/${r.id}/finish`, { weight_g: +w, support_g: 0, photos, note }); toast.success("Retouch dicatat"); onSaved(); onClose(); } catch (e) { err(e); }
  };
  const other = mine && !r;
  return (
    <Modal open={!!job} onClose={onClose} xwide title={`Retouch · ${job?.wo_code || ""} · ${job?.file_name || ""}`}
      footer={r ? <Btn data-testid="retouch-finish" disabled={!(+w > 0) || !photos.length} onClick={finish}><Square className="w-4 h-4" /> Selesai & Simpan</Btn> : null}>
      <p className="text-xs text-slate-500 mb-2">Lepas support, assembly, pengeleman, dan lainnya digabung jadi satu. Bisa dikerjakan beberapa orang; timer dihitung per orang per hari.</p>
      <WoNotes notes={job?.wo_notes} tid="retouch-wo-notes" />
      {(job?.wo_images || job?.preview_images)?.length > 0 && <div className="flex flex-wrap gap-2 my-2" data-testid="retouch-images">{(job.wo_images?.length ? job.wo_images : job.preview_images).map((p) => <img key={p} src={fileUrl(p)} alt="bentuk" className="h-24 rounded-lg border object-contain bg-white" />)}</div>}
      <RetouchProgress r={progress} tid="retouch-progress" />
      {other && <p className="text-sm text-rose-600 mt-3" data-testid="retouch-other">Anda masih punya sesi retouch aktif di {mine.wo_code} · {mine.file_name}. Selesaikan dulu.</p>}
      {!r && !other && !job?.retouch_done_at && <Btn className="mt-3" data-testid="retouch-start" onClick={() => call(`/prod/jobs/${job.id}/retouch/start`, {}, "Timer retouch mulai")}><Play className="w-4 h-4" /> Mulai Retouch</Btn>}
      {r && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-3">
            <span className="font-mono text-3xl" data-testid="retouch-timer">{hms(secs(r))}</span>
            <Btn variant="ghost" data-testid="retouch-pause" onClick={() => call(`/prod/retouch/${r.id}/pause`, {}, r.status === "PAUSED" ? "Lanjut" : "Dijeda — silakan pantau mesin")}>{r.status === "PAUSED" ? <><Play className="w-4 h-4" /> Lanjut</> : <><Pause className="w-4 h-4" /> Jeda (pantau mesin)</>}</Btn>
            <button type="button" className="text-xs text-slate-400 underline ml-auto" data-testid="retouch-cancel" onClick={() => { if (window.confirm("Batalkan sesi ini?")) call(`/prod/retouch/${r.id}/cancel`, {}, "Sesi dibatalkan"); }}>batalkan</button>
          </div>
          <Field label="Berat benda dikerjakan (g)"><Input type="number" data-testid="retouch-weight" value={w} onChange={(e) => setW(e.target.value)} /></Field>
          <MediaInput testid="retouch-photos" label="Foto hasil (wajib, maks 2)" max={2} value={photos} onChange={setPhotos} />
          <Field label="Catatan"><Input data-testid="retouch-note" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        </div>
      )}
      {job?.wo_id && <RetouchDaily wid={job.wo_id} onDone={onSaved} />}
    </Modal>
  );
};
